import type { Id, ISODate, Month, Page, Version } from './common';
import type { ProjectKind, ProjectStatus } from './projects';

/**
 * The finance wire shapes (spec §2, §3, §5). Every amount is integer đồng as an exact decimal string — never a
 * JavaScript number — so a 38-digit figure survives the trip from `numeric(38,0)` to the screen. A plain amount
 * is positive; a bank balance, an opening, an adjustment and `minBalance` are signed. Percentages are strings
 * with two decimals (`"28.13"`).
 */
export type Money = string;

export const DIRECTIONS = ['in', 'out'] as const;
export type Direction = typeof DIRECTIONS[number];
export const ENTRY_STATUSES = ['done', 'overdue', 'upcoming'] as const;
export type EntryStatus = typeof ENTRY_STATUSES[number];
/** `open` = no actual yet (overdue + upcoming). */
export type EntryStatusFilter = EntryStatus | 'open';
export const PROJECT_FILTERS = ['open', 'active', 'paused', 'closed', 'all'] as const;
/** `open` = active + paused (the default). */
export type FinanceProjectFilter = typeof PROJECT_FILTERS[number];

export type CategoryDTO = { id: Id; name: string; direction: Direction; hidden: boolean; sortOrder: number;
  entryCount: number; version: Version };
export type CategoryBody = { name: string; direction: Direction };
export type CategoryPatchBody = { name?: string; hidden?: boolean; sortOrder?: number; version: Version };

export type EntryProjectRef = { id: Id; code: string; name: string; status: ProjectStatus };
export type EntryDTO = { id: Id; project: EntryProjectRef; category: { id: Id; name: string; hidden: boolean };
  direction: Direction; title: string; plannedAmount: Money; plannedDate: ISODate;
  actualAmount: Money | null; actualDate: ISODate | null; note: string | null;
  status: EntryStatus; daysLate: number | null;
  /** `null` when the row was not generated by a template, or was detached from it. */
  recurring: { id: Id; month: Month } | null;
  /** `actualDate` is on or before the latest reconciliation: the books are closed for it. */
  locked: boolean;
  version: Version };
export type EntryCreateBody = { projectId: Id; categoryId: Id; title: string; plannedAmount: Money; plannedDate: ISODate;
  actualAmount?: Money; actualDate?: ISODate; note?: string | null };
export type EntryPatchBody = Partial<Omit<EntryCreateBody, 'actualAmount' | 'actualDate'>> & { version: Version };
export type ActualBody =
  | { actualAmount: Money; actualDate: ISODate; remainder?: { plannedDate: ISODate } | null; version: Version }
  | { clear: true; version: Version };
export type ActualResult = { entry: EntryDTO; remainder: EntryDTO | null };
export type EntryQuery = { year?: number; month?: Month; projectId?: Id; categoryId?: Id; direction?: Direction;
  status?: EntryStatusFilter; cursor?: string; limit?: number;
  /** A title search, folded like every search (S5 D157); at most 100 characters. */
  q?: string;
  /** Only entries of projects the reader sees — what a card may be linked to (L4 F1). */
  linkable?: '1' };
export type EntryPage = Page<EntryDTO>;

export type ProjectMetrics = { plannedIn: Money; actualIn: Money; plannedOut: Money; actualOut: Money;
  /** `"28.13"`; `null` for an internal project or when nothing is planned as income. */
  margin: string | null;
  /** `marginGap` = target − margin, set only when short. */
  marginShort: boolean; marginGap: string | null;
  overBudget: boolean; overBudgetBy: 'planned' | 'actual' | null;
  overBudgetAmount: Money | null; overBudgetPercent: string | null;
  /** actualOut / costBudget × 100. */
  budgetUsePercent: string | null;
  /** contractValue − plannedIn, when positive. */
  unplannedIn: Money | null;
  /** `profitToDate` = actualIn − actualOut. */
  overdueIn: number; profitToDate: Money };
export type FinanceProjectDTO = { id: Id; code: string; name: string; kind: ProjectKind; status: ProjectStatus;
  startDate: ISODate | null; endDate: ISODate | null; manager: { id: Id; name: string } | null;
  investors: { id: Id; name: string }[]; contractValue: Money | null;
  /** `financeVersion` 0 = no `project_finance` row yet. */
  costBudget: Money | null; targetMargin: string | null; financeVersion: Version;
  metrics: ProjectMetrics };
export type FinanceProjectDetailDTO = FinanceProjectDTO & { entryCount: number; overdueCount: number };
export type ProjectFinancePatchBody = { costBudget: Money | null; targetMargin: string | null; version: Version };

export type MonthColumn = { month: Month; phase: 'past' | 'current' | 'future';
  actualIn: Money | null; actualOut: Money | null; plannedIn: Money | null; plannedOut: Money | null;
  net: Money; closing: Money | null };
export type OverdueRow = { id: Id; title: string; project: { id: Id; code: string; name: string };
  amount: Money; plannedDate: ISODate; daysLate: number };
export type AlertCause =
  | { kind: 'out'; entryId: Id; title: string; projectName: string; amount: Money }
  | { kind: 'in'; entryId: Id; title: string; projectName: string; amount: Money; month: Month };
export type ThresholdAlert = { month: Month; forecast: Money; minBalance: Money; cause: AlertCause | null };
/** `rows` capped at 20, oldest first. */
export type OverdueList = { rows: OverdueRow[]; total: Money; count: number };
export type OverviewDTO = { year: number; years: number[]; hasOpening: boolean; openingYear: number | null;
  today: ISODate; currentMonth: Month;
  balance: { current: Money; lastReconciliation: { date: ISODate; bankBalance: Money } | null;
    openingDate: ISODate; sinceMonthStart: Money | null } | null;
  monthIn: { actual: Money; planned: Money; overdueCount: number };
  monthOut: { actual: Money; planned: Money };
  yearEnd: { balance: Money; lowest: { month: Month; balance: Money } | null } | null;
  months: MonthColumn[]; minBalance: Money | null; alert: ThresholdAlert | null;
  overdueIn: OverdueList; overdueOut: OverdueList };

export type BalanceRowDTO = { id: Id | null; kind: 'opening' | 'reconciliation' | 'year_start'; date: ISODate;
  bankBalance: Money; bookBalanceAt: Money | null; adjustment: Money | null;
  recordedBy: { id: Id; name: string } | null; version: Version | null };
/** `rows` newest first. */
export type BalanceYearDTO = { year: number; years: number[];
  opening: { date: ISODate; amount: Money; version: Version; locked: boolean } | null;
  yearStart: Money | null; lastReconciliation: { date: ISODate; bankBalance: Money; adjustment: Money } | null;
  adjustmentTotal: Money; rows: BalanceRowDTO[] };
export type BalanceBody = { kind: 'opening'; date: ISODate; amount: Money }
  | { kind: 'reconciliation'; date: ISODate; bankBalance: Money };
export type OpeningPatchBody = { date: ISODate; amount: Money; version: Version };
export type ThresholdDTO = { minBalance: Money | null; forecastMonths: number; version: Version };
export type ThresholdBody = ThresholdDTO;

export type RecurringDTO = { id: Id; title: string; project: EntryProjectRef; category: { id: Id; name: string };
  amount: Money; dayOfMonth: number; startMonth: Month; endMonth: Month | null; active: boolean;
  /** active and (endMonth null or ≥ the current month). */
  running: boolean;
  version: Version };
export type RecurringListDTO = { items: RecurringDTO[]; monthlyTotal: Money; runningCount: number };
export type RecurringBody = { title: string; projectId: Id; categoryId: Id; amount: Money; dayOfMonth: number;
  startMonth: Month; endMonth: Month | null };
export type RecurringPatchBody = Partial<RecurringBody> & { version: Version };
export type VersionBody = { version: Version };
